/* eslint-disable react-doctor/no-inline-exhaustive-style, react-doctor/no-tiny-text */
"use client";

/**
 * PostDownloadReceipt — G3.3 (TES-1238)
 *
 * The primary post-download success surface on /sounds/[id]. Replaces the
 * transient Sonner toast: instead of a 6-second confirmation that vanishes, the
 * download CTA morphs into this inline "lock sound receipt" card (Direction A +
 * receipt framing from the TES-897 kickoff, approved 2026-07-01). No modal — the
 * install path stays on the page, never obscured (PRODUCT.md Principle #5).
 *
 * Contents:
 *   - Confirmation + receipt identity (serial, date, size, 44.1k/16 mono line)
 *   - The 3 install steps inline (ruthlessly clear — docs/ANTI_SLOP.md rule #4)
 *   - Share via Web Share API (feature-detected; hidden when unsupported) with a
 *     copy-link fallback that always works
 *   - A quiet "did it work?" help link + a link back to the library
 *   - "Remind me tomorrow" — localStorage only, no email capture
 *
 * All Tesla facts come from lib/tesla-facts.ts. Both themes are driven by the
 * --tls-* token layer (no hard-coded colors), so light/dark are first-class.
 */

import { useCallback, useEffect, useRef, useState, type Ref } from "react";
import Link from "next/link";
import { Check, Share2, Link2, ArrowRight, Image as ImageIcon } from "lucide-react";
import { Chrome } from "@/components/ui/tls/chrome";
import { EmailSoundForm } from "@/components/ui/email-sound-form";
import {
  markInstallAnswered,
  recordPendingInstall,
} from "@/components/ui/install-checkin";
import { Mono } from "@/components/ui/tls/mono";
import { TESLA_FACTS } from "@/lib/tesla-facts";
import { fmtFileSize } from "@/lib/tls-format";
import { CONDENSED_INSTALL_STEPS } from "@/lib/install-steps";
import {
  trackDownloadConfirmShown,
  trackInstallSelfReportedComplete,
  trackReferralLinkGenerated,
  trackReferralLinkCopied,
  trackShareBackClick,
} from "@/lib/tracking";
import { buildSoundShareBack, buildSurfaceShareUrl } from "@/lib/social-share-copy";
import { announce } from "@/lib/announce";
import { readStorage, removeStorage, writeStorage } from "@/lib/safe-storage";

const SITE_URL =
  process.env.NEXT_PUBLIC_SITE_URL?.replace(/\/$/, "") || "https://teslalocksound.com";

const REMIND_PREFIX = "tls.remindMe.";
/** "Remind me tomorrow" horizon — 36h so a next-evening visit still counts. */
const REMIND_TTL_MS = 36 * 60 * 60 * 1000;

export interface PostDownloadReceiptSound {
  id: string;
  title: string;
  slug?: string;
  category: string;
  /** Human file-size string from the sounds table, e.g. "129 KB". */
  size?: string;
}

export interface PostDownloadReceiptProps {
  sound: PostDownloadReceiptSound;
  /** First related sound — surfaced as a quiet "also grabbed" nudge. */
  followOnSound?: { id: string; title: string; slug?: string } | null;
  className?: string;
  /** Opens the ShareSetupPanel modal (share-card generator); button hidden when absent. */
  onCreateShareCard?: () => void;
  /** Re-triggers the download flow (e.g. USB drive wiped, need the file again); link hidden when absent. */
  onDownloadAgain?: () => void;
  /**
   * The "You're set." heading, for a caller that wants to bring the visitor
   * back to it later (the sticky mobile CTA does, once the download is done).
   */
  headingRef?: Ref<HTMLHeadingElement>;
  /**
   * §05.3: focus moves to the panel heading after a download completes. On by
   * default because this receipt only ever mounts as the result of the
   * visitor's own click; pass false when mounting it without that gesture.
   */
  autoFocusHeading?: boolean;
}

/**
 * Deterministic 4-digit "serial" derived from the sound id so the same sound
 * always prints the same receipt number (stable across renders and shares).
 */
function receiptSerial(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    h = (h * 31 + id.charCodeAt(i)) >>> 0;
  }
  return String(h % 10000).padStart(4, "0");
}

export function PostDownloadReceipt({
  sound,
  followOnSound,
  className,
  onCreateShareCard,
  onDownloadAgain,
  headingRef,
  autoFocusHeading = true,
}: PostDownloadReceiptProps) {
  const canonicalUrl = `${SITE_URL}/sounds/${sound.slug ?? sound.id}`;

  // The heading is where focus lands after the download (§05.3, WCAG 2.4.3):
  // the Download button the visitor pressed unmounts, and without this the
  // browser drops focus to <body>. Internal ref for the focus call, merged
  // with the caller's so the sticky CTA can return here later.
  const headingEl = useRef<HTMLHeadingElement | null>(null);
  const setHeadingRef = useCallback(
    (node: HTMLHeadingElement | null) => {
      headingEl.current = node;
      if (typeof headingRef === "function") headingRef(node);
      else if (headingRef) headingRef.current = node;
    },
    [headingRef]
  );
  useEffect(() => {
    if (!autoFocusHeading) return;
    // On iOS Safari the download opens IOSDownloadOverlay (a modal dialog)
    // in the same tick; it owns focus until dismissed and hands it to this
    // heading on close, so do not pull focus out from behind it.
    if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
    headingEl.current?.focus();
  }, [autoFocusHeading]);
  const shareUrl = buildSurfaceShareUrl(canonicalUrl, "receipt");
  const shareBack = buildSoundShareBack(SITE_URL, sound.slug ?? sound.id, sound.title, sound.id);
  const handleShareBackClick = useCallback(
    (channel: "x" | "reddit") => {
      trackShareBackClick({ sound_id: sound.id, sound_title: sound.title, channel });
    },
    [sound.id, sound.title]
  );

  // Capture the download moment once (client-only — this card only mounts after
  // a successful download, so there is no SSR/hydration date mismatch).
  const [downloadedAt] = useState(() => new Date());
  const dateLabel = downloadedAt.toISOString().slice(0, 10);
  const serial = receiptSerial(sound.id);
  // One file-size format on the template (F-4): "342 KB" / "1.9 MB", never
  // the raw catalog string ("1996.1 KB").
  const sizeLabel = fmtFileSize(sound.size) || "≤ 1 MB";

  // Web Share support is feature-detected after mount so SSR and first client
  // render agree (both assume unsupported), then the button reveals if present.
  const [canNativeShare, setCanNativeShare] = useState(false);
  useEffect(() => {
    // Feature-detect after mount so SSR and first client render agree (both
    // assume unsupported), then reveal the button if the API exists.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCanNativeShare(typeof navigator !== "undefined" && "share" in navigator);
  }, []);

  const [copied, setCopied] = useState(false);
  const [remind, setRemind] = useState(false);

  useEffect(() => {
    const raw = readStorage(REMIND_PREFIX + sound.id);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as { exp?: number };
      // Sync the persisted opt-in into state after hydration.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      if (parsed?.exp && parsed.exp > Date.now()) setRemind(true);
    } catch {
      // Corrupt entry — the reminder is a nicety, not load-bearing.
    }
  }, [sound.id]);

  // Arm the return-visit install check-in: the next sounds-page visit 2h-14d
  // from now asks "did the install work?" (components/ui/install-checkin.tsx).
  useEffect(() => {
    recordPendingInstall(sound.id, sound.title);
  }, [sound.id, sound.title]);

  // Impression event — the denominator for every post-download metric.
  //
  // This card is the real post-download surface: /sounds/[id] suppresses the
  // fuller PostDownloadPanel (`showPostDownloadPanel={false}`, TES-1238) and
  // renders this instead, so nearly every download ends here rather than there.
  // Until now only the panel reported an impression, which meant post-download
  // rates were being read against `download_completed` — a denominator that
  // cannot distinguish "surface never rendered" from "rendered and ignored".
  // A 2026-07-30 funnel analysis drew the wrong conclusion for exactly that
  // reason. Same event as the panel, split by `surface`, so the two are
  // directly comparable.
  //
  // Mount == impression: the card only renders behind `downloadCompleted` on
  // the sound-detail page, and the ref keeps a re-render from double-counting.
  const confirmTracked = useRef(false);
  useEffect(() => {
    if (confirmTracked.current) return;
    confirmTracked.current = true;
    trackDownloadConfirmShown({
      sound_id: sound.id,
      sound_title: sound.title,
      surface: "sound_detail",
    });
  }, [sound.id, sound.title]);

  const [installOutcome, setInstallOutcome] = useState<"success" | "failed" | null>(null);
  const handleInstallReport = useCallback(
    (outcome: "success" | "failed") => {
      markInstallAnswered(sound.id, outcome);
      trackInstallSelfReportedComplete({
        outcome,
        sound_id: sound.id,
        source: "receipt",
        came_from_download: true,
      });
      setInstallOutcome(outcome);
      announce(
        outcome === "success" ? "Enjoy the new chime." : "7 common fixes, sorted in 5 minutes.",
        "polite"
      );
    },
    [sound.id]
  );

  const handleCopy = useCallback(async () => {
    trackReferralLinkGenerated({
      sound_id: sound.id,
      share_surface: "post_download_receipt",
    });
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      announce("Link copied.", "polite");
      window.setTimeout(() => setCopied(false), 1600);
      trackReferralLinkCopied({
        sound_id: sound.id,
        share_surface: "post_download_receipt",
        share_method: "clipboard",
      });
    } catch {
      // Clipboard blocked (permissions / insecure context) — no-op; the visible
      // link text is still selectable as a manual fallback.
    }
  }, [shareUrl, sound.id]);

  const handleNativeShare = useCallback(async () => {
    trackReferralLinkGenerated({
      sound_id: sound.id,
      share_surface: "post_download_receipt",
    });
    try {
      await navigator.share({
        title: `${sound.title}: Tesla Lock Sound`,
        text: `Check out this Tesla lock sound: ${sound.title}`,
        url: shareUrl,
      });
      trackReferralLinkCopied({
        sound_id: sound.id,
        share_surface: "post_download_receipt",
        share_method: "native_share",
      });
    } catch (error) {
      // User dismissed the OS share sheet — stay silent (no error surface).
      if ((error as DOMException)?.name === "AbortError") return;
    }
  }, [shareUrl, sound.id, sound.title]);

  const toggleRemind = useCallback(() => {
    setRemind((prev) => {
      const next = !prev;
      // Non-fatal when storage is unavailable — the reminder simply won't
      // persist on this device.
      if (next) {
        writeStorage(
          REMIND_PREFIX + sound.id,
          JSON.stringify({ v: 1, exp: Date.now() + REMIND_TTL_MS })
        );
      } else {
        removeStorage(REMIND_PREFIX + sound.id);
      }
      return next;
    });
  }, [sound.id]);

  // Install steps — ruthlessly clear, no jokes (Principle #5). Wording comes
  // verbatim from CONDENSED_INSTALL_STEPS (lib/install-steps.ts) so every
  // post-download surface agrees; this component only styles the
  // filename/path/menu tokens inside those canonical strings.
  const { filename, path } = TESLA_FACTS.usb;
  const menuPath = "Toybox → Boombox → Lock Sound";
  const tokenPattern = new RegExp(
    `(${[path, filename, menuPath]
      .map(t => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .join("|")})`,
    "g",
  );
  const highlightTokens = (text: string) =>
    text.split(tokenPattern).map((part, i) => {
      if (part === filename || part === path) {
        return (
          <code key={i} style={codeStyle}>
            {part}
          </code>
        );
      }
      if (part === menuPath) {
        return (
          <span key={i} style={{ color: "var(--tls-fg-strong)", fontWeight: 500 }}>
            {part}
          </span>
        );
      }
      return part;
    });
  const installSteps = CONDENSED_INSTALL_STEPS.map(step => highlightTokens(step.body));

  return (
    <div
      // §05.3: the panel is role=status aria-live=polite, so the confirmation
      // and the three install steps are announced when they appear. §11.1's
      // objection stands for the rest of the panel: the share row and the
      // footer (copy feedback, install outcome, remind toggle) are marked
      // aria-live="off" below so their state changes do not broadcast twice —
      // lib/announce.ts's single shell region already speaks the two worth
      // speaking (link copied, install outcome) from their handlers.
      role="status"
      aria-live="polite"
      className={className}
      style={{
        // De-carded. The receipt metaphor argued for an edge, but rule 1 does
        // not carve out an exception for a metaphor: this is content, so it
        // gets hairlines rather than an enclosure. Rules top and bottom keep
        // the stub reading as a discrete slip without boxing it, which is what
        // a real receipt has anyway — a tear, not a border.
        borderTop: "1px solid var(--tls-border-subtle)",
        borderBottom: "1px solid var(--tls-border-subtle)",
        overflow: "hidden",
      }}
    >
      {/* ── Receipt stub ─────────────────────────────────────────────────── */}
      <div style={{ padding: "20px 22px 16px" }}>
        <Chrome>lock sound receipt</Chrome>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginTop: 12,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 26,
              height: 26,
              borderRadius: "var(--tls-radius-pill)",
              background: "color-mix(in srgb, var(--tls-brand-ink) 16%, transparent)",
              color: "var(--tls-brand-ink)",
              flexShrink: 0,
            }}
          >
            <Check className="size-4" />
          </span>
          <h2
            ref={setHeadingRef}
            id="post-download-receipt-heading"
            // Programmatic focus target only (§05.3); not in the Tab order.
            tabIndex={-1}
            className="tlsr-focus"
            style={{
              margin: 0,
              fontFamily: "var(--tls-font-display)",
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: "var(--tls-fg-strong)",
              // The sticky CTA scrolls here with block:start; keep the heading
              // clear of the fixed nav.
              scrollMarginTop: "calc(var(--tls-nav-h) + 1.5rem)",
            }}
          >
            You&apos;re set.
          </h2>
        </div>
        <p
          style={{
            margin: "8px 0 0",
            fontSize: 14,
            color: "var(--tls-fg-muted)",
          }}
        >
          <Mono size={13} color="var(--tls-brand)">
            {filename}
          </Mono>{" "}
          saved to your downloads.
        </p>
        <div style={{ marginTop: 10 }}>
          <Mono size={11} color="var(--tls-fg-subtle)" aria-hidden>
            No. {serial} · {dateLabel} · {sizeLabel} · 44.1k/16
          </Mono>
        </div>
      </div>

      {/* Perforated divider — the "receipt" tear line, per theme via tokens. */}
      <div
        aria-hidden="true"
        style={{
          borderTop: "1px dashed var(--tls-border-strong, var(--tls-border-subtle))",
          margin: "0 22px",
        }}
      />

      {/* ── Install steps (sacred path — always visible, no scroll) ───────── */}
      <div style={{ padding: "16px 22px" }}>
        <Chrome color="var(--tls-fg-muted)">install · 3 steps</Chrome>
        <ol
          style={{
            margin: "12px 0 0",
            padding: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {installSteps.map((step, i) => (
            <li
              key={i}
              style={{
                display: "flex",
                gap: 12,
                fontSize: 13.5,
                lineHeight: 1.5,
                color: "var(--tls-fg-muted)",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  flexShrink: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 22,
                  height: 22,
                  borderRadius: "var(--tls-radius-pill)",
                  border: "1px solid var(--tls-border-subtle)",
                  background: "var(--tls-surface-elevated)",
                  fontFamily: "var(--tls-font-body)",
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 600,
                  fontSize: 11,
                  color: "var(--tls-fg-strong)",
                }}
              >
                {i + 1}
              </span>
              <span style={{ paddingTop: 1 }}>{step}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* ── Share row ────────────────────────────────────────────────────── */}
      <div
        aria-live="off"
        style={{
          padding: "16px 22px",
          borderTop: "1px solid var(--tls-border-subtle)",
        }}
      >
        <Chrome color="var(--tls-fg-muted)">share</Chrome>
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          {canNativeShare && (
            <button
              type="button"
              onClick={handleNativeShare}
              className="tlsr-focus"
              style={shareButtonStyle}
            >
              <Share2 className="size-4" aria-hidden="true" />
              Share sound
            </button>
          )}
          <button
            type="button"
            onClick={handleCopy}
            aria-label="Copy link to this sound"
            className="tlsr-focus"
            style={shareButtonStyle}
          >
            {copied ? (
              <>
                <Check className="size-4" aria-hidden="true" />
                Copied
              </>
            ) : (
              <>
                <Link2 className="size-4" aria-hidden="true" />
                Copy link
              </>
            )}
          </button>
          <a
            href={shareBack.x}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleShareBackClick("x")}
            className="tlsr-focus"
            style={shareButtonStyle}
          >
            Post on X
          </a>
          <a
            href={shareBack.reddit}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleShareBackClick("reddit")}
            className="tlsr-focus"
            style={shareButtonStyle}
          >
            Post on Reddit
          </a>
          {onCreateShareCard && (
            <button
              type="button"
              onClick={onCreateShareCard}
              className="tlsr-focus"
              style={shareButtonStyle}
            >
              <ImageIcon className="size-4" aria-hidden="true" />
              Create a share card
            </button>
          )}
        </div>
        {followOnSound && (
          <p
            style={{
              margin: "12px 0 0",
              fontSize: 12.5,
              color: "var(--tls-fg-subtle)",
            }}
          >
            People who grabbed this also downloaded{" "}
            <Link
              href={`/sounds/${followOnSound.slug ?? followOnSound.id}`}
              style={{ color: "var(--tls-brand)", textDecoration: "none" }}
              className="hover:underline"
            >
              {followOnSound.title}
            </Link>
            .
          </p>
        )}
      </div>

      {/* ── Footer: did-it-work help, library link, remind-me ────────────── */}
      <div
        aria-live="off"
        style={{
          padding: "14px 22px 18px",
          borderTop: "1px solid var(--tls-border-subtle)",
          background: "var(--tls-surface-elevated)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          {installOutcome === "success" ? (
            <span style={{ fontSize: 13, color: "var(--tls-fg-muted)" }}>
              Enjoy the new chime.
            </span>
          ) : installOutcome === "failed" ? (
            <Link
              href="/blog/troubleshooting-tesla-custom-sounds"
              style={{ fontSize: 13, color: "var(--tls-fg-strong)", textDecoration: "underline" }}
            >
              7 common fixes, sorted in 5 minutes
            </Link>
          ) : (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                color: "var(--tls-fg-muted)",
              }}
            >
              Installed it already?
              <button
                type="button"
                onClick={() => handleInstallReport("success")}
                className={reportBtnHitArea}
                style={reportBtnStyle}
              >
                It worked
              </button>
              <button
                type="button"
                onClick={() => handleInstallReport("failed")}
                className={reportBtnHitArea}
                style={reportBtnStyle}
              >
                Had trouble
              </button>
            </span>
          )}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 14 }}>
            {onDownloadAgain && (
              <button
                type="button"
                onClick={onDownloadAgain}
                // 24px of painted text; the ::before pads the target to 44.
                className="tlsr-focus relative before:absolute before:inset-x-0 before:-inset-y-2.5 before:content-['']"
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
                <Mono size={12} color="var(--tls-fg-muted)">
                  Download again
                </Mono>
              </button>
            )}
            <Link
              href="/sounds"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 13,
                color: "var(--tls-fg-muted)",
                textDecoration: "none",
              }}
              // 20px of painted text; the ::before pads the target to 44.
              className="relative transition-colors before:absolute before:inset-x-0 before:-inset-y-3 before:content-[''] hover:text-[color:var(--tls-fg-strong)]"
            >
              Browse more sounds
              <ArrowRight className="size-3.5" aria-hidden="true" />
            </Link>
          </span>
        </div>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            // 8px, not less: the row above pads its 32px buttons 6px
            // downward, and a closer label would sit on top of that pad.
            marginTop: 8,
            // The label is the checkbox's hit area: a 14px box alone is not
            // a thumb target, the full-width 44px row is.
            minHeight: 44,
            fontSize: 12.5,
            color: "var(--tls-fg-subtle)",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={remind}
            onChange={toggleRemind}
            style={{ width: 14, height: 14, accentColor: "var(--tls-brand)" }}
          />
          Remind me tomorrow to try it. Saved on this device, no email.
        </label>

        {/* Email-me bridge — for the mobile user whose USB drive is elsewhere;
            emails the download link + the same 3 steps. */}
        <div
          style={{ marginTop: 12, borderTop: "1px solid var(--tls-border-subtle)", paddingTop: 4 }}
        >
          <EmailSoundForm
            soundId={sound.id}
            surface="post_download_receipt"
            label="Email me this file"
            helper="Downloaded on your phone? We'll send the link and install steps to open on your computer."
          />
        </div>
      </div>
    </div>
  );
}

const codeStyle: React.CSSProperties = {
  fontFamily: "var(--tls-font-mono)",
  fontSize: "0.86em",
  color: "var(--tls-fg-strong)",
  background: "var(--tls-surface-elevated)",
  border: "1px solid var(--tls-border-subtle)",
  borderRadius: 4,
  padding: "1px 5px",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
};

/**
 * §02.4's padded-wrapper pattern (as components/ui/button.tsx `sm` does): the
 * painted box stays 32px, the ::before extends the hit area to the 44px
 * target PRODUCT.md requires wherever a thumb is expected. 7px, not 6: an
 * absolutely positioned pseudo-element is inset from the PADDING box, so the
 * pill's 1px border on each side is not part of it - measured 42px with 6.
 */
const reportBtnHitArea =
  "tlsr-focus relative before:absolute before:inset-x-0 before:-inset-y-[7px] before:content-['']";

const reportBtnStyle: React.CSSProperties = {
  minHeight: 32,
  padding: "4px 12px",
  fontSize: 12.5,
  fontWeight: 500,
  borderRadius: "var(--tls-radius-pill)",
  border: "1px solid var(--tls-border-subtle)",
  background: "var(--tls-surface-elevated)",
  color: "var(--tls-fg-strong)",
  cursor: "pointer",
};

const shareButtonStyle: React.CSSProperties = {
  flex: "1 1 130px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  minHeight: 44,
  padding: "0 14px",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: "var(--ls-radius-0)",
  border: "1px solid var(--tls-border-subtle)",
  background: "var(--tls-surface)",
  color: "var(--tls-fg-strong)",
  cursor: "pointer",
};

export default PostDownloadReceipt;
